let dpopKeyPairPromise = null;
let publicJwkPromise = null;

function getCrypto() {
  const cryptoApi = typeof crypto !== "undefined" ? crypto : globalThis.crypto;
  if (!cryptoApi || !cryptoApi.subtle) {
    throw new Error("Web Crypto is required for DPoP proofs.");
  }
  return cryptoApi;
}

function stringToBytes(value) {
  return new TextEncoder().encode(value);
}

function bytesToBase64Url(bytes) {
  const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join("");
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64UrlToBytes(value) {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(padded);
  return Uint8Array.from(binary, character => character.charCodeAt(0));
}

function normalizeHtu(url) {
  let normalized;
  try {
    normalized = new URL(url);
  } catch (_error) {
    const base =
      typeof window !== "undefined" && window.location.origin && window.location.origin !== "null"
        ? window.location.origin
        : undefined;
    normalized = new URL(url, base);
  }
  normalized.search = "";
  normalized.hash = "";
  return normalized.toString();
}

function derToJoseSignature(signature) {
  const bytes = new Uint8Array(signature);
  if (bytes.length === 64) {
    return bytes;
  }

  if (bytes[0] !== 0x30) {
    return bytes;
  }

  let offset = 2;
  if (bytes[1] & 0x80) {
    offset = 2 + (bytes[1] & 0x7f);
  }

  if (bytes[offset] !== 0x02) {
    return bytes;
  }
  const rLength = bytes[offset + 1];
  const r = bytes.slice(offset + 2, offset + 2 + rLength);
  offset += 2 + rLength;

  if (bytes[offset] !== 0x02) {
    return bytes;
  }
  const sLength = bytes[offset + 1];
  const s = bytes.slice(offset + 2, offset + 2 + sLength);

  const jose = new Uint8Array(64);
  jose.set(r.slice(Math.max(0, r.length - 32)), 32 - Math.min(32, r.length));
  jose.set(s.slice(Math.max(0, s.length - 32)), 64 - Math.min(32, s.length));
  return jose;
}

function defaultNowSeconds() {
  return Math.floor(Date.now() / 1000);
}

function defaultRandomId() {
  const cryptoApi = getCrypto();
  if (typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  return bytesToBase64Url(bytes);
}

async function sha256Base64Url(value) {
  const hash = await getCrypto().subtle.digest("SHA-256", stringToBytes(value));
  return bytesToBase64Url(new Uint8Array(hash));
}

export async function getDpopKeyPair() {
  if (!dpopKeyPairPromise) {
    dpopKeyPairPromise = getCrypto().subtle.generateKey(
      { name: "ECDSA", namedCurve: "P-256" },
      true,
      ["sign", "verify"]
    );
  }
  return dpopKeyPairPromise;
}

export async function getDpopPublicJwk() {
  if (!publicJwkPromise) {
    publicJwkPromise = getDpopKeyPair().then(pair => getCrypto().subtle.exportKey("jwk", pair.publicKey));
  }
  return publicJwkPromise;
}

export async function createDpopProof({
  url,
  method,
  accessToken,
  nowSeconds = defaultNowSeconds,
  randomId = defaultRandomId
} = {}) {
  if (!url) {
    throw new Error("createDpopProof requires url.");
  }

  const publicJwk = await getDpopPublicJwk();
  const header = {
    typ: "dpop+jwt",
    alg: "ES256",
    jwk: {
      kty: publicJwk.kty,
      crv: publicJwk.crv,
      x: publicJwk.x,
      y: publicJwk.y
    }
  };
  const payload = {
    jti: randomId(),
    htm: String(method || "GET").toUpperCase(),
    htu: normalizeHtu(url),
    iat: nowSeconds()
  };

  if (accessToken) {
    payload.ath = await sha256Base64Url(accessToken);
  }

  const encodedHeader = bytesToBase64Url(stringToBytes(JSON.stringify(header)));
  const encodedPayload = bytesToBase64Url(stringToBytes(JSON.stringify(payload)));
  const signingInput = `${encodedHeader}.${encodedPayload}`;
  const keyPair = await getDpopKeyPair();
  const signature = await getCrypto().subtle.sign(
    { name: "ECDSA", hash: { name: "SHA-256" } },
    keyPair.privateKey,
    stringToBytes(signingInput)
  );

  return `${signingInput}.${bytesToBase64Url(derToJoseSignature(signature))}`;
}

export function decodeJwtPartForTest(encodedPart) {
  return JSON.parse(new TextDecoder().decode(base64UrlToBytes(encodedPart)));
}
